import { getDate } from '@homesusa/core';
import { FieldRulesProps } from '../interfaces/field-rules.interface';
import { InputRulesMessages } from '../interfaces/field-rules-messages';

export const isNotNumber = (value: unknown): boolean => {
  return isNaN(Number(value));
};

export const isNullOrEmpty = (value: unknown): boolean => {
  return value === null || value === undefined || value === '';
};

export const isStringNullOrEmpty = (value: string): boolean => {
  return isNullOrEmpty(value) || value.trim().length === 0;
};

const toString = (value: unknown): string => {
  if (isNullOrEmpty(value)) return '';

  return typeof value === 'string' || value instanceof String ? (value as string) : `${value}`;
};

// return true is value match the regex
export const checkRegex = (value: unknown, regex: string | undefined | null): boolean => {
  if (regex == null) {
    return true;
  }
  const regexp = new RegExp(regex);
  return regexp.test(toString(value));
};

const greaterOrEqualThan = (value: unknown, min: unknown): boolean => {
  return min != null && value != null && Number(value) >= Number(min);
};
type numberType = number | undefined | null;

// return true if value is greater or equal than min
export const checkMinLimit = (value: unknown, min: numberType): boolean => {
  return min == null || greaterOrEqualThan(value, min);
};

// return true if value is less or equal than max
export const checkMaxLimit = (value: unknown, max: numberType): boolean => {
  return max == null || greaterOrEqualThan(max, value);
};

// return true if value length is greater or equal than minLength
export const checkMinLength = (value: unknown, minLength: numberType): boolean => {
  return minLength == null || greaterOrEqualThan(toString(value).length, minLength);
};

// return true if value length is less or equal than maxLength
export const checkMaxLength = (value: unknown, maxLength: numberType): boolean => {
  return maxLength == null || greaterOrEqualThan(maxLength, toString(value).length);
};

// using for input[type=number], input[type=text] and textarea
export const getTextFieldError = (
  value: unknown,
  rules: {
    regex?: string;
    regexMessage?: string;
    minLength?: number;
    maxLength?: number;
  },
  lengthType: 'characters' | 'digits',
  customMessages?: InputRulesMessages
): string | null => {
  if (!checkRegex(value, rules.regex)) {
    return rules.regexMessage ?? 'Invalid value';
  }

  if (!checkMinLength(value, rules.minLength)) {
    return customMessages?.min ?? `Must have at least ${rules.minLength} ${lengthType}`;
  }

  if (!checkMaxLength(value, rules.maxLength)) {
    return customMessages?.max ?? `Must have less than ${rules.maxLength} ${lengthType}`;
  }

  return null;
};

// using for input[type=number], input[type=text] and textarea
export const getTextFieldWarning = (
  value: unknown,
  rules: {
    minLengthSuggestion?: number;
  },
  lengthType: 'characters' | 'digits'
): string | null => {
  if (!checkMinLength(value, rules.minLengthSuggestion)) {
    return `Enter a total of ${rules.minLengthSuggestion} or more ${lengthType} to market your listing more effectively.`;
  }
  return null;
};

export const isValidDate = (date: unknown): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

export const isValidMilitaryTime = (value: string | null | undefined): boolean => {
  return checkRegex(value, '^([01]\\d|2[0-3]):([0-5]\\d)$');
};

// return true if date is less (or equal than) min
export const checkMinDate = (
  value: Date,
  minValue: Date | undefined | null,
  equalThan?: boolean
): boolean => {
  const date = new Date(value.setHours(0, 0, 0, 0));
  const min = getDate(minValue ?? undefined);
  if (min == null || !isValidDate(min)) {
    return true;
  }
  const minDate = new Date(min.setHours(0, 0, 0, 0));
  return !!equalThan ? date <= minDate : date < minDate;
};

// return true if date is greater (or equal than) max
export const checkMaxDate = (
  value: Date,
  maxValue: Date | undefined | null,
  equalThan?: boolean
): boolean => {
  const date = new Date(value.setHours(0, 0, 0, 0));
  const max = getDate(maxValue ?? undefined);
  if (max == null || !isValidDate(max)) {
    return true;
  }
  const maxDate = new Date(max.setHours(0, 0, 0, 0));
  return !!equalThan ? date >= maxDate : date > maxDate;
};

export const getRuleErrors = (rules: FieldRulesProps, value: unknown): string[] => {
  const errors: string[] = [];

  if (!checkRegex(value, rules.regex)) {
    errors.push(rules.regexMessage ?? 'invalid value');
  }

  if (rules.numberFormat) {
    if (isNaN(Number(value))) {
      errors.push(`must be numeric`);
    } else {
      if (!checkMinLimit(value, rules.numberFormat.min)) {
        errors.push(`must be equal or more than ${rules.numberFormat.min}`);
      }

      if (!checkMaxLimit(value, rules.numberFormat.max)) {
        errors.push(`must be equal or less than ${rules.numberFormat.max}`);
      }
    }
  }

  if (!checkMinLength(value, rules.minLength)) {
    errors.push(
      `Must have at least ${rules.minLength} ${
        rules.numberFormat != null ? 'digits' : 'characters'
      }`
    );
  }

  if (!checkMaxLength(value, rules.maxLength)) {
    errors.push(
      `must have less than ${rules.maxLength} ${
        rules.numberFormat != null ? 'digits' : 'characters'
      }`
    );
  }

  return errors;
};
